// ── Body scroll lock ─────────────────────────────────────────────────────────
//
// Overlays that cover the viewport (mobile nav drawer, user detail drawer, the
// calculation modal) need to stop the page behind them scrolling.
//
// The obvious implementation is unsafe:
//
//   const prev = document.body.style.overflow;   // ← captures "hidden" if
//   document.body.style.overflow = "hidden";     //   another lock is active
//   return () => { document.body.style.overflow = prev; };
//
// When two of these overlap, the inner one captures the outer one's "hidden"
// and restores *that* on close, so the lock never lifts. `theme.css` sets
// `html, body { height: 100% }`, so a stuck `body { overflow: hidden }` makes
// body a viewport-height scroll container that clips everything below the fold
// — the page silently becomes unscrollable on desktop and mobile alike while
// the content is still laid out at full height underneath.
//
// This module reference-counts instead: the original value is captured once by
// the first lock and only restored when the last one releases.

let lockCount = 0;
let savedOverflow = null;

/**
 * Lock body scrolling. Returns an idempotent release function — calling it more
 * than once is a no-op, so it is safe to use directly as an effect cleanup.
 */
export function lockBodyScroll() {
  if (typeof document === "undefined") return () => {};

  if (lockCount === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  lockCount += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.style.overflow = savedOverflow ?? "";
      savedOverflow = null;
    }
  };
}

/**
 * Escape hatch: drop every outstanding lock and restore scrolling.
 * For recovery paths (e.g. route changes) where a lock may have been orphaned
 * by an unmount that never ran its cleanup.
 */
export function releaseAllBodyScrollLocks() {
  if (typeof document === "undefined") return;
  lockCount = 0;
  document.body.style.overflow = savedOverflow ?? "";
  savedOverflow = null;
}
